import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, UserPlus, Building2, Phone, Mail } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [status, setStatus] = useState('all');
  const [employmentType, setEmploymentType] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (search) params.append('search', search);
      if (department !== 'all') params.append('department', department);
      if (status !== 'all') params.append('status', status);
      if (employmentType !== 'all') params.append('employment_type', employmentType);

      const response = await axios.get(`${API_URL}/api/employees?${params}`);
      setEmployees(response.data.employees || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load employees');
      // Demo data
      setEmployees([
        { id: '1', employee_id: 'EMP0001', first_name: 'Rahul', last_name: 'Sharma', email: 'rahul@lunipin.com', mobile_number: '+91 98765 43210', department: 'Engineering', designation: 'Senior Developer', employee_status: 'Active', employment_type: 'Full-time', date_of_joining: '2023-01-15' },
        { id: '2', employee_id: 'EMP0002', first_name: 'Priya', last_name: 'Patel', email: 'priya@lunipin.com', mobile_number: '+91 98765 43211', department: 'HR', designation: 'HR Manager', employee_status: 'Active', employment_type: 'Full-time', date_of_joining: '2022-06-01' },
        { id: '3', employee_id: 'EMP0003', first_name: 'Amit', last_name: 'Kumar', email: 'amit@lunipin.com', mobile_number: '+91 98765 43212', department: 'Finance', designation: 'Accountant', employee_status: 'On Notice', employment_type: 'Full-time', date_of_joining: '2023-03-20' },
        { id: '4', employee_id: 'EMP0004', first_name: 'Sneha', last_name: 'Gupta', email: 'sneha@lunipin.com', mobile_number: '+91 98765 43213', department: 'Marketing', designation: 'Marketing Lead', employee_status: 'Active', employment_type: 'Full-time', date_of_joining: '2022-09-10' },
        { id: '5', employee_id: 'EMP0005', first_name: 'Vikram', last_name: 'Singh', email: 'vikram@lunipin.com', mobile_number: '+91 98765 43214', department: 'Engineering', designation: 'Intern', employee_status: 'Active', employment_type: 'Intern', date_of_joining: '2024-01-05' },
      ]);
      setPagination(prev => ({ ...prev, total: 5, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, department, status, employmentType]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments`);
      setDepartments(response.data.departments || []);
    } catch {
      setDepartments(['Engineering', 'HR', 'Finance', 'Marketing', 'Operations', 'Sales']);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [fetchEmployees]);

  const getEmploymentTypeBadge = (type) => {
    const colors = {
      'Full-time': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Part-time': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Contract': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Intern': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    return colors[type] || colors['Full-time'];
  };

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={row.photograph_url} />
            <AvatarFallback className="bg-green-500/20 text-green-400 text-sm font-bold">
              {row.first_name?.charAt(0)}{row.last_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-white">{row.first_name} {row.last_name}</p>
            <p className="text-xs text-slate-500 mono">{row.employee_id}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (row) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-sm text-slate-300">
            <Mail className="w-3 h-3 text-slate-500" />
            {row.email}
          </div>
          <div className="flex items-center gap-1 text-sm text-slate-400">
            <Phone className="w-3 h-3 text-slate-500" />
            {row.mobile_number}
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (row) => (
        <div>
          <div className="flex items-center gap-1 text-slate-300">
            <Building2 className="w-3 h-3 text-slate-500" />
            {row.department}
          </div>
          <p className="text-xs text-slate-500">{row.designation}</p>
        </div>
      ),
    },
    {
      key: 'employment_type',
      header: 'Type',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getEmploymentTypeBadge(row.employment_type)}`}>
          {row.employment_type}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.employee_status} />,
    },
    {
      key: 'joining',
      header: 'Joined',
      render: (row) => <span className="text-slate-400">{formatDate(row.date_of_joining)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`employee-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => navigate(`/employees/${row.id}/edit`)}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View / Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="employees-page">
      <PageHeader
        title="Employees"
        description="Manage employee records and information"
        action={() => navigate('/employees/new')}
        actionLabel="Add Employee"
        actionIcon={UserPlus}
      />

      <SearchFilter
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, email, employee ID..."
        filters={[
          {
            key: 'department',
            placeholder: 'Department',
            value: department,
            onChange: setDepartment,
            options: [
              { value: 'all', label: 'All Departments' },
              ...departments.map(d => ({ value: d, label: d }))
            ],
          },
          {
            key: 'status',
            placeholder: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: 'All Status' },
              { value: 'Active', label: 'Active' },
              { value: 'On Notice', label: 'On Notice' },
              { value: 'Relieved', label: 'Relieved' },
              { value: 'Absconded', label: 'Absconded' },
            ],
          },
          {
            key: 'type',
            placeholder: 'Type',
            value: employmentType,
            onChange: setEmploymentType,
            options: [
              { value: 'all', label: 'All Types' },
              { value: 'Full-time', label: 'Full-time' },
              { value: 'Part-time', label: 'Part-time' },
              { value: 'Contract', label: 'Contract' },
              { value: 'Intern', label: 'Intern' },
            ],
          },
        ]}
        onReset={() => {
          setSearch('');
          setDepartment('all');
          setStatus('all');
          setEmploymentType('all');
        }}
      />

      <DataTable
        columns={columns}
        data={employees}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No employees found"
      />
    </div>
  );
}
